import {
  computed, observable, makeObservable,
} from 'mobx';
import { Axios } from '../utilities/network';
import { isWeb } from '../utilities/platform';
import RootStore from './root-store';
import { GetLocalStorage, SetLocalStorage } from '../utilities/cryptography';
import { QR_CODE_TOKEN, USER_INFO } from '../constants/storage-keys';
import { navigate } from 'gatsby';
import { CURRENT_PLATFORM } from '../constants/settings';
import { Gender, PAGE_SIZE } from '../constants/options';
import { ProfileTemplate } from '../utilities/profile';
import { GetLangKey, GetUserProfile, Language } from '../utilities/general';

export default class PatientStore {

  loading: boolean = false;

  loadingHistory: boolean = false;
  loadingReport: boolean = false;


  loadingHistoryBg: boolean = false;

  creating: boolean = false;

  updating: boolean = false;

  uploading: boolean = false;

  removing: boolean = false;

  copying: boolean = false;

  processingHistory: boolean = false;

  getOptions: boolean = false;

  loadingCameraToken: boolean = false;

  loadingTokenWebInfo: boolean = false;

  loadingTokenPhoneInfo: boolean = false;

  uploadingCameraAttachments: boolean = false;

  uploadingPatientConsent: boolean = false;


  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      loading: observable,
      loadingHistory: observable,
      creating: observable,
      updating: observable,
      uploading: observable,
      removing: observable,
      processingHistory: observable,
      getOptions: observable,
      copying: observable,
      loadingCameraToken: observable,
      loadingHistoryBg: observable,
      loadingTokenWebInfo: observable,
      loadingTokenPhoneInfo: observable,
      uploadingCameraAttachments: observable,
      loadingReport: observable,
    });
    this.rootStore = rootStore;
  }


  searchPatient = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}, lang: Language = GetLangKey()) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Patient/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, { ...reqModel, clinicId: GetUserProfile().currentClinicId });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  getPatient = async (id: number, lang: Language = Language.ENGLISH) => {
    this.loading = true;
    try {
      const res = await Axios.get(`/api/Patient/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  addPatient = async (reqModel: any, lang: Language = GetLangKey()) => {
    this.creating = true;
    try {
      const res = await Axios.post(`/api/Patient?lang=${lang}&platformId=${CURRENT_PLATFORM}`, { ...reqModel, isValidationRequired: true });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.creating = false;
    }
  };

  checkPatient = async (reqModel: any) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Patient/Check?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  updatePatient = async (reqModel: any, lang: Language = GetLangKey()) => {
    this.updating = true;
    try {
      const res = await Axios.put(`/api/Patient?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.updating = false;
    }
  };

  searchPatientHistory = async (skip: number, take: number = PAGE_SIZE, reqModel: any, lang: Language = GetLangKey()) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, { ...reqModel, clinicId: GetUserProfile().currentClinicId });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  createPatientHistory = async (reqModel: any, lang: Language = GetLangKey()) => {
    this.processingHistory = true;
    try {
      const res = await Axios.post(`/api/Patient/History?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.processingHistory = false;
    }
  };

  updatePatientHistory = async (reqModel: any, isLoadingDisabled: boolean = false, lang: Language = GetLangKey()) => {
    // this.updating = true;
    if (!isLoadingDisabled) this.processingHistory = true;
    try {
      const res = await Axios.put(`/api/Patient/History?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      // this.updating = false;
      if (!isLoadingDisabled) this.processingHistory = false;
    }
  };

  removeMultiplePatientHistory = async (ids: number[]) => {
    this.removing = true;
    let req = { ids: ids };
    try {
      const res = await Axios.post(`/api/Patient/History/Delete?platformId=${CURRENT_PLATFORM}`, req);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.removing = false;
    }
  };


  removePatientHistory = async (id: number) => {
    this.removing = true;
    try {
      const res = await Axios.delete(`/api/Patient/History/${id}?platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.removing = false;
    }
  };

  getPatientHistory = async (id: number, lang: Language = GetLangKey()) => {
    this.loading = true;
    try {
      const res = await Axios.get(`/api/Patient/History/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  getPatientHistoryDiagnosis = async (id: number) => {
    this.loading = true;
    try {
      const res = await Axios.get(`/api/Patient/History/Diagnosis/${id}?platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  uploadHistoryAttachments = async (reqModel: any, lang: Language = GetLangKey()) => {
    this.uploading = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Attachments?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploading = false;
    }
  };

  removeHistoryAttachment = async (id: number) => {
    this.removing = true;
    try {
      const res = await Axios.delete(`/api/Patient/History/Attachment/${id}?platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.removing = false;
    }
  };

  searchAllergenOptions = async (reqModel: any = {}) => {
    this.getOptions = true;
    try {
      const res = await Axios.post(`/api/Option/Allergen/Search/0/0?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.getOptions = false;
    }
  };

  searchSymptomsOptions = async (reqModel: any = {}) => {
    this.getOptions = true;
    try {
      const res = await Axios.post(`/api/Option/Symptom/Search/0/0?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.getOptions = false;
    }
  };


  copyPatientHistory = async (preId: number, practitionerId: number) => {
    this.copying = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Copy?preId=${preId}&practitionerId=${practitionerId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.copying = false;
    }
  };


  searchPatientHistoryPayment = async (skip: number, take: number, reqModel: any) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Payment/Search/${skip}/${take}?platformId=${CURRENT_PLATFORM}`, { ...reqModel });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };


  getComplaintByHistoryId = async (historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.get(`/api/Patient/History/Diagnosis/Complaint?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  updateComplaintByHistoryId = async (reqModel: any, historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Diagnosis/Complaint?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };
  //Create new history
  createDiagnosis = async (patientId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Diagnosis/?patientId=${patientId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  getAuxiliaryTestByHistoryId = async (historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.get(`/api/Patient/History/Diagnosis/AuxiliaryTest?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  updateAuxiliaryTestByHistoryId = async (reqModel: any, historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Diagnosis/AuxiliaryTest?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  getPreviousHistoryByHistoryId = async (historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.get(`/api/Patient/History/Diagnosis/PreviousHistory?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  updatePreviousHistoryByHistoryId = async (reqModel: any, historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Diagnosis/PreviousHistory?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  getPersonalHealthByHistoryId = async (historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.get(`/api/Patient/History/Diagnosis/PersonalHealth?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  updatePersonalHealthByHistoryId = async (reqModel: any, historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Diagnosis/PersonalHealth?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  getSummaryByHistoryId = async (historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.get(`/api/Patient/History/Diagnosis/Summary?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  updateSummaryByHistoryId = async (reqModel: any, historyId: number) => {
    this.loadingHistory = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Diagnosis/Summary?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHistory = false;
    }
  };

  // generateCameraToken = async (req: any) => {
  //   this.loadingCameraToken = true;
  //   try {
  //     const res = await Axios.post(`/api/Patient/History/Camera/Token?platformId=${CURRENT_PLATFORM}`, req);
  //     SetLocalStorage(QR_CODE_TOKEN, { ...res.data, localCreateTime: Date.now() });
  //     return Promise.resolve(res.data);
  //   } catch (err) {
  //     return Promise.reject(err);
  //   } finally {
  //     this.loadingCameraToken = false;
  //   }
  // };

  GenerateCameraToken = async (
    historyId: number,
    sectionKey: string,
    valueKey: string | number,
    targetId?: number
  ) => {
    this.loadingCameraToken = true;
    try {
      const updateRep: any = {
        patientHistoryId: historyId,
        diagnosisSession: sectionKey,
        diagnosisValue: String(valueKey),
        targetObjectId: targetId,
      };
      const localQRToken: any = await GetLocalStorage(QR_CODE_TOKEN);
      // console.log('GenerateCameraToken localQRToken', localQRToken, localQRToken.patientHistoryId === Number(historyId));
      if ((localQRToken && Date.now() - localQRToken.localCreateTime <= 30 * 60 * 1000) && localQRToken?.patientHistoryId === Number(historyId)) {
        const uploadRes = await Axios.put(`/api/Patient/History/Camera/Token?token=${localQRToken.token}&platformId=${CURRENT_PLATFORM}`, updateRep);
        const newLocalQRToken = { ...localQRToken, ...uploadRes };
        SetLocalStorage(QR_CODE_TOKEN, newLocalQRToken);
        return Promise.resolve(newLocalQRToken);
      } else {
        const res = await Axios.post(`/api/Patient/History/Camera/Token?platformId=${CURRENT_PLATFORM}`, updateRep);
        SetLocalStorage(QR_CODE_TOKEN, { ...res.data, localCreateTime: Date.now() });
        return Promise.resolve(res.data);
      }
    } catch (err) {
      SetLocalStorage(QR_CODE_TOKEN, null);
      return Promise.reject(err);
    } finally {
      this.loadingCameraToken = false;
    }
  };

  updateTokenInfo = async (token: string, diagnosisSession: string, diagnosisValue: string, targetObjectId?: number) => {
    this.loadingTokenWebInfo = true;
    let req: any = { diagnosisSession, diagnosisValue };
    if (targetObjectId) req.targetObjectId = targetObjectId;
    try {
      const res = await Axios.put(`/api/Patient/History/Camera/Token?token=${token}&platformId=${CURRENT_PLATFORM}`, req);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingTokenWebInfo = false;
    }
  };


  getCameraWebInfoByToken = async (token: string, diagnosisName: string = "Examination") => {
    this.loadingTokenWebInfo = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Camera/${diagnosisName}/WebInfo?token=${token}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingTokenWebInfo = false;
    }
  };

  getCameraPhoneInfoByToken = async (token: string, diagnosisName: string = "") => {
    this.loadingTokenPhoneInfo = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Camera/${diagnosisName}/PhoneInfo?token=${token}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingTokenPhoneInfo = false;
    }
  };

  updateCameraTokenText = async (token: string, diagnosisName: string = "", req: any) => {
    this.loadingTokenPhoneInfo = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Camera/${diagnosisName}/Text?token=${token}&platformId=${CURRENT_PLATFORM}`, req);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingTokenPhoneInfo = false;
    }
  };

  uploadCameraAttachments = async (reqModel: any, token: string) => {
    this.uploadingCameraAttachments = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Camera/Attachment?token=${token}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploadingCameraAttachments = false;
    }
  };


  getPatientHistoryPaymentReport = async (reqModel: any) => {
    this.loadingReport = true;
    try {
      const res = await Axios.post(`/api/Patient/History/Payment/Report?platformId=${CURRENT_PLATFORM}`, { ...reqModel });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingReport = false;
    }
  };

  uploadPatientConsent = async (reqModel: any) => {
    this.uploadingPatientConsent = true;
    try {
      const res = await Axios.post(`/api/Patient/Onboard/Consent/Form/Generate?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploadingPatientConsent = false;
    }
  };

  uploadPatientConsentByToken = async (reqModel: any, token: string) => {
    this.uploadingPatientConsent = true;
    try {
      const res = await Axios.post(`/api/Patient/Consent/Form/Token/Generate?token=${token}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploadingPatientConsent = false;
    }
  };

}